import { Dayjs } from "dayjs";

/**
 * How a Cash Flow view picks its months. Every preset is rolling: it's
 * recomputed from today, so "Last 6 months" keeps meaning the last 6 months.
 * The "last N months" presets count the current month as one of the N.
 */
export type DateRangePreset =
  | "this-month"
  | "last-month"
  | "year-to-date"
  | "last-6-months"
  | "last-12-months"
  | "custom";

export const DATE_RANGE_PRESET_LABELS: Record<DateRangePreset, string> = {
  "this-month": "This month",
  "last-month": "Last month",
  "year-to-date": "Year to date",
  "last-6-months": "Last 6 months",
  "last-12-months": "Last 12 months",
  custom: "Custom",
};

export interface ReportDateRange {
  preset: DateRangePreset;
  /** The chosen months, which only apply to the "custom" preset. */
  startMonth: Dayjs | null;
  endMonth: Dayjs | null;
}

export function presetRange(
  preset: DateRangePreset,
  today: Dayjs,
): { beginDate: Dayjs; endDate: Dayjs } | null {
  const thisMonth = today.startOf("month");
  const throughThisMonth = (beginMonth: Dayjs) => ({
    beginDate: beginMonth,
    endDate: thisMonth.endOf("month"),
  });
  switch (preset) {
    case "this-month":
      return throughThisMonth(thisMonth);
    case "last-month": {
      const lastMonth = thisMonth.subtract(1, "month");
      return { beginDate: lastMonth, endDate: lastMonth.endOf("month") };
    }
    case "year-to-date":
      return throughThisMonth(today.startOf("year"));
    case "last-6-months":
      return throughThisMonth(thisMonth.subtract(5, "month"));
    case "last-12-months":
      return throughThisMonth(thisMonth.subtract(11, "month"));
    case "custom":
      return null;
  }
}

/**
 * The first and last days the range covers. A custom range whose pickers have
 * been cleared falls back to the start of the end month's year and to the
 * current month.
 */
export function resolveDateRange(
  range: ReportDateRange,
  today: Dayjs,
): { beginDate: Dayjs; endDate: Dayjs } {
  const preset = presetRange(range.preset, today);
  if (preset) return preset;

  const endDate = (range.endMonth ?? today).endOf("month");
  const beginDate = range.startMonth
    ? range.startMonth.startOf("month")
    : endDate.startOf("year");
  return { beginDate, endDate };
}

/**
 * Switching to "custom" starts the month pickers on the range that was showing,
 * so the user adjusts it rather than starting from blank pickers.
 */
export function selectDateRangePreset(
  range: ReportDateRange,
  preset: DateRangePreset,
  today: Dayjs,
): ReportDateRange {
  if (preset !== "custom") return { ...range, preset };
  const { beginDate, endDate } = resolveDateRange(range, today);
  return {
    preset,
    startMonth: beginDate.startOf("month"),
    endMonth: endDate.startOf("month"),
  };
}
